import { FFmpeg } from "@ffmpeg/ffmpeg";
import { fetchFile, toBlobURL } from "@ffmpeg/util";
import { useState, useCallback, useRef } from "react";

export interface VideoMetadata {
  width: number;
  height: number;
  fps: number;
  duration: number;
  frame_count: number;
}

export interface UseVideoMetadataReturn {
  extractMetadata: (file: File) => Promise<VideoMetadata>;
  extractMetadataFromBase64: (base64String: string) => Promise<VideoMetadata>;
  isLoading: boolean;
  error: string | null;
  calculateTotalMegapixels: (metadata: VideoMetadata, scale: number) => number;
  validateMetadata: (metadata: VideoMetadata, scale: number, limitDuration?: number) => void;
}

export const useVideoMetadata = (): UseVideoMetadataReturn => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const ffmpegRef = useRef<FFmpeg | null>(null);

  const initializeFFmpeg = useCallback(async () => {
    if (!ffmpegRef.current) {
      ffmpegRef.current = new FFmpeg();
    }

    if (!ffmpegRef.current.loaded) {
      const baseURL = "https://unpkg.com/@ffmpeg/core@0.12.6/dist/umd";
      await ffmpegRef.current.load({
        coreURL: await toBlobURL(`${baseURL}/ffmpeg-core.js`, "text/javascript"),
        wasmURL: await toBlobURL(`${baseURL}/ffmpeg-core.wasm`, "application/wasm"),
      });
    }

    return ffmpegRef.current;
  }, []);

  const processVideoData = useCallback(
    async (videoData: Uint8Array): Promise<VideoMetadata> => {
      const ffmpeg = await initializeFFmpeg();

      // ファイルをffmpegに書き込み
      await ffmpeg.writeFile("input.mp4", videoData);

      // ffprobeコマンドでメタデータを取得
      // ffmpeg.wasmではffprobeは直接使用できないため、ffmpegコマンドで情報を取得
      let stdout = "";
      ffmpeg.on("log", ({ message }) => {
        if (message.includes("Duration:") || message.includes("Video:")) {
          stdout += message + "\n";
        }
      });

      try {
        // 1フレーム目を出力することでメタデータを取得
        await ffmpeg.exec(["-i", "input.mp4", "-vframes", "1", "-f", "null", "-"]);
      } catch {
        // ffmpegのログから情報を抽出するため、エラーは無視
      }

      // ffmpegのログからメタデータを解析
      const durationMatch = stdout.match(/Duration: (\d+):(\d+):(\d+)\.(\d+)/);
      const videoMatch = stdout.match(/Video: .+?, (\d+)x(\d+).+?, (.+?) fps/);

      if (!durationMatch || !videoMatch) {
        throw new Error("ffmpegからメタデータを取得できませんでした");
      }

      // 時間を秒に変換
      const hours = parseInt(durationMatch[1]);
      const minutes = parseInt(durationMatch[2]);
      const seconds = parseInt(durationMatch[3]);
      const milliseconds = parseInt(durationMatch[4]);
      const duration = hours * 3600 + minutes * 60 + seconds + milliseconds / 100;

      // 解像度とFPSを取得
      const width = parseInt(videoMatch[1]);
      const height = parseInt(videoMatch[2]);
      const fps = parseFloat(videoMatch[3]);

      // バックエンドと同じ方法でフレーム数を計算
      const frame_count = Math.floor(fps * duration);

      // ファイルクリーンアップ
      await ffmpeg.deleteFile("input.mp4");

      return {
        width,
        height,
        fps: Math.round(fps),
        duration,
        frame_count,
      };
    },
    [initializeFFmpeg]
  );

  const extractMetadata = useCallback(
    async (file: File): Promise<VideoMetadata> => {
      setIsLoading(true);
      setError(null);

      try {
        const videoData = await fetchFile(file);
        return await processVideoData(videoData);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : "メタデータの抽出に失敗しました";
        setError(errorMessage);
        throw new Error(errorMessage);
      } finally {
        setIsLoading(false);
      }
    },
    [processVideoData]
  );

  const extractMetadataFromBase64 = useCallback(
    async (base64String: string): Promise<VideoMetadata> => {
      setIsLoading(true);
      setError(null);

      try {
        // Base64からBinary dataを取得
        const base64Data = base64String.split(",")[1];
        const binaryString = atob(base64Data);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }

        return await processVideoData(bytes);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : "メタデータの抽出に失敗しました";
        setError(errorMessage);
        throw new Error(errorMessage);
      } finally {
        setIsLoading(false);
      }
    },
    [processVideoData]
  );

  // バックエンドのcalculate_total_megapixelsと同じ計算
  const calculateTotalMegapixels = useCallback((metadata: VideoMetadata, scale: number): number => {
    try {
      const width = metadata.width * scale;
      const height = metadata.height * scale;
      const pixelsPerFrame = width * height;
      const mpPerFrame = pixelsPerFrame / (1000 * 1000); // メガピクセルに変換
      const totalFrames = Math.floor(metadata.fps * metadata.duration);
      return Math.floor(mpPerFrame * totalFrames);
    } catch {
      throw new Error("メガピクセル計算に失敗しました");
    }
  }, []);

  // バックエンドのvalidate_metadataと同じバリデーション
  const validateMetadata = useCallback((metadata: VideoMetadata, scale: number, limitDuration = 60): void => {
    // 4K解像度の制限（3840 x 2160 = 8,294,400ピクセル）
    if (metadata.width * metadata.height > 8294400) {
      throw new Error("解像度が4Kを超えています");
    }

    // 時間制限
    if (metadata.duration > limitDuration) {
      throw new Error(`動画の長さが${limitDuration}秒を超えています`);
    }

    // スケール後の解像度チェック
    const scaledWidth = metadata.width * scale;
    const scaledHeight = metadata.height * scale;
    if (scaledWidth * scaledHeight > 8294400) {
      throw new Error("スケール後の解像度が4Kを超えています");
    }

    // フレーム数の制限（REALESRGANの場合）
    if (metadata.frame_count >= 1000) {
      throw new Error("フレーム数が1000を超えています");
    }
  }, []);

  return {
    extractMetadata,
    extractMetadataFromBase64,
    isLoading,
    error,
    calculateTotalMegapixels,
    validateMetadata,
  };
};
